import { TransformedSuperGridColDef } from '@aleph/supergrid';
import { FileType } from 'rsuite/Uploader';
import { useMutation } from '@apollo/client';
import {
	CreativeType,
	MediaInput,
	UploadRedditMediaDocument,
} from 'wb-reddit/graphql/generated';
import { RedditLevel } from 'wb-reddit/src/app/routes/superGrid/utils';
import { NoValuePlaceholder, UploadCellComponent, WaveformIcon } from '@aleph/shared-components';
import { ICellRendererParams } from 'ag-grid-community';
import { captureException } from '@sentry/react';

export const supportedImageFormats = ['jpg', 'jpeg', 'png', 'gif'];
export const supportedVideoFormats = ['mp4'];

export function getFileNameExtension(fileName: string): string {
	const nameSegments = fileName.split('.') ?? [];

	if (nameSegments.length < 2) {
		return '';
	}

	return nameSegments.at(-1)?.toLowerCase() ?? '';
}

const RedditMediaCellRenderer = (params: ICellRendererParams & { disabled?: boolean }) => {
	const colDef = params.colDef as TransformedSuperGridColDef<RedditLevel>;
	const [uploadMedia, { loading }] = useMutation(UploadRedditMediaDocument);

	const onUpload = async (files: FileType[]) => {
		const fileExtension = getFileNameExtension(files[0].name || '');

		let media: MediaInput = {
			image: files[0].blobFile,
		};

		switch (params.data.creativeType) {
			case CreativeType.Video:
				if (!supportedVideoFormats.includes(fileExtension)) {
					params?.setValue?.(null);

					return;
				}
				media = {
					video: files[0].blobFile,
				};

				break;
			case CreativeType.Carousel:
			case CreativeType.Image:
			default:
				if (!supportedImageFormats.includes(fileExtension)) {
					params?.setValue?.(null);

					return;
				}
				media = {
					image: files[0].blobFile,
				};

				break;
		}

		try {
			const { data: uploadMediaData } = await uploadMedia({
				variables: {
					media,
					clientId: params.data.parent?.parent?.parent?.clientId,
				},
			});

			params?.setValue?.(uploadMediaData?.uploadMedia);
		} catch (e) {
			captureException(e);
		}
	};

	if (
		params.data.isCreate || (params.disabled)
		|| (colDef?.level && colDef.level !== params.data.level)
		|| params.data.creativeType === CreativeType.Text
		|| (params.data.supergridIsNoDataRow && !params.data.supergridIsCreateRow)
	) {
		return <NoValuePlaceholder />;
	}

	if (!params.value) {
		return (
			<UploadCellComponent
				loading={loading}
				mediaUrl={params.data.mediaThumbnail}
				mediaName={params.data.mediaName}
				onUpload={onUpload}
				accept={params.data.creativeType === CreativeType.Video
					? supportedVideoFormats.map((format) => `video/${format}`).join(', ')
					: supportedImageFormats.map((format) => `image/${format}`).join(', ')}
			/>
		);
	}

	return (
		<div className="flex gap-[8px] items-center h-full overflow-hidden overflow-ellipsis">
			{
				params.value.type === 'AUDIO'
					? (
						<div className="h-full aspect-square flex items-center justify-center">
							<WaveformIcon className="w-[16px] h-[16px]" />
						</div>
					)
					: (
						<img
							alt="media"
							className="h-full aspect-square object-cover"
							src={params.value.mediaThumbnail ?? params.value.mediaUrl}
						/>
					)
			}

			<div className='overflow-hidden overflow-ellipsis'>
				{params.value.name ?? params.value.mediaUrl}
			</div>
		</div>
	);
};

export default RedditMediaCellRenderer;
